import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { MlyGrid } from './shared/MlyGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { withShortLabels } from './shared/rowGroupsTransposed'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useMonthlyData } from '../../hooks/useMonthlyData'
import { useMonthlyNormals } from '../../hooks/useMonthlyNormals'

const SLUG = 'mly-vitr'

const ROW_GROUPS = [
  {
    label: 'Průměrná rychlost větru',
    rows: [
      { tsId: 'WSAVG_AVG', label: 'Průměrná rychlost větru',     unit: 'm/s', normalMode: 'diff', decimals: 1 },
      { tsId: 'WSAVG_MAX', label: 'Maximum průměrné rychlosti',   unit: 'm/s', normalMode: null,   decimals: 1 },
    ],
  },
  {
    label: 'Dny s průměrnou rychlostí větru',
    rows: [
      { tsId: 'WSAVG_G10', label: 'Větrné dny – průměr > 10 m/s',    unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'WSAVG_G20', label: 'Velmi větrné – průměr > 20 m/s',   unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'WSAVG_G30', label: 'Bouřlivé – průměr > 30 m/s',       unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
  {
    label: 'Nárazy větru',
    rows: [
      { tsId: 'WSMAX_MAX',  label: 'Maximum nárazu větru',      unit: 'm/s', normalMode: 'diff', decimals: 1 },
      { tsId: 'WSMAX_G10',  label: 'Dny s nárazy ≥ 10 m/s',    unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'WSMAX_G20',  label: 'Dny s nárazy ≥ 20 m/s',    unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'WSMAX_G30',  label: 'Dny s nárazy ≥ 30 m/s',    unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
  {
    label: 'Tlak vzduchu',
    rows: [
      { tsId: 'PRESSAVG_AVG',    label: 'Průměrný tlak na stanici',         unit: 'hPa', normalMode: 'diff', decimals: 1 },
      { tsId: 'PRESSAVG_MAX',    label: 'Maximum tlaku',                     unit: 'hPa', normalMode: null,   decimals: 1 },
      { tsId: 'PRESSAVG_MIN',    label: 'Minimum tlaku',                     unit: 'hPa', normalMode: null,   decimals: 1 },
      { tsId: 'PRESSSEAAVG_AVG', label: 'Průměrný tlak na hladině moře',    unit: 'hPa', normalMode: 'diff', decimals: 1 },
      { tsId: 'PRESSSEAAVG_MAX', label: 'Maximum tlaku na HM',               unit: 'hPa', normalMode: null,   decimals: 1 },
      { tsId: 'PRESSSEAAVG_MIN', label: 'Minimum tlaku na HM',               unit: 'hPa', normalMode: null,   decimals: 1 },
    ],
  },
]

// 2. definice hlavičky – transponovaná tabulka.
const ROW_GROUPS_T = withShortLabels(ROW_GROUPS, {
  WSAVG_AVG: 'Průměr', WSAVG_MAX: 'Max. průměru',
  WSAVG_G10: '> 10 m/s', WSAVG_G20: '> 20 m/s', WSAVG_G30: '> 30 m/s',
  WSMAX_MAX: 'Max. náraz', WSMAX_G10: '≥ 10 m/s', WSMAX_G20: '≥ 20 m/s', WSMAX_G30: '≥ 30 m/s',
  PRESSAVG_AVG: 'Průměr', PRESSAVG_MAX: 'Maximum', PRESSAVG_MIN: 'Minimum',
  PRESSSEAAVG_AVG: 'Průměr (HM)', PRESSSEAAVG_MAX: 'Maximum (HM)', PRESSSEAAVG_MIN: 'Minimum (HM)',
}, {
  'Průměrná rychlost větru': 'Prům. rychlost',
  'Dny s průměrnou rychlostí větru': 'Dny – průměr',
  'Nárazy větru': 'Nárazy',
})

const TS_IDS = ROW_GROUPS.flatMap(g => g.rows.map(r => r.tsId))

export function MlyVitrView({ station }) {
  const { year, setYear } = useYearPeriodStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const { dataMap, yearMap, dtMap, yearDtMap, isLoading: dLoading, isError: dError } = useMonthlyData(station?.gh_id, year, TS_IDS)
  const { normalsMap, yearNormalsMap, isLoading: nLoading }        = useMonthlyNormals(station?.gh_id, TS_IDS)

  const isLoading = dLoading || nLoading

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner size="sm" /> Načítání dat…</div>}
        {dError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
        {!isLoading && !dError && (
          <MlyGrid
            rowGroups={ROW_GROUPS}
            rowGroupsTransposed={ROW_GROUPS_T}
            orientation={orientation}
            dataMap={dataMap}
            yearMap={yearMap}
            dtMap={dtMap}
            yearDtMap={yearDtMap}
            normalsMap={normalsMap}
            yearNormalsMap={yearNormalsMap}
          />
        )}
      </div>
    </div>
  )
}
